import React, { useCallback, useMemo } from "react";
import { Dropdown, Empty, Grid, Spin, Table, Tag } from "antd";
import { useEditTrapMenu } from "../../hooks/useEditTrapMenu";
import { useAppDispatch } from "../../hooks/reduxHook";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import "./index.css";
import { setWifiModalOpen, selectDevice } from "../../reducers/devicesSlice";
import { Device } from "../../types";
import { useFallbackPollingInterval } from "../../providers/RealtimeProvider";
import {
  DEVICE_STATUS_LABELS,
  DeviceStatusTag,
  deviceStatusTag,
} from "../../utils/deviceState";

export const DEVICE_STATUS_POLL_MS = 30000;

interface DeviceTableRecord {
  key: string;
  deviceName: string;
  location: string;
  address: string;
  coordinates: string;
  wifiConnection: string;
  status: DeviceStatusTag;
}

const StatusTag: React.FC<{ status: DeviceStatusTag }> = ({ status }) => (
  <Tag className={`table-device-status-tag ${status}`}>
    {DEVICE_STATUS_LABELS[status]}
  </Tag>
);

const TrapTable: React.FC<{}> = () => {
  const { wrapRow, menuFor } = useEditTrapMenu();
  const dispatch = useAppDispatch();
  // Six columns do not fit a phone or a portrait tablet, and the two that
  // matter most in the field - wifi and status - are the last two, so below lg
  // each trap becomes a card that leads with them instead.
  const screens = Grid.useBreakpoint();
  const compact = !screens.lg;

  const { data: trapSiteData, isLoading: isTrapSitesLoading } =
    useListTrapSitesQuery();
  // Status changes arrive over the realtime socket while it is open. While it
  // is down - or when this build has no socket - the list is polled instead,
  // so someone standing at a trap waiting for it to come online still sees the
  // change within about 30s.
  const pollingInterval = useFallbackPollingInterval(DEVICE_STATUS_POLL_MS);
  const { data: devices, isLoading: isDevicesLoading } = useListDevicesQuery(
    undefined,
    { pollingInterval },
  );

  const deviceData = useMemo((): DeviceTableRecord[] => {
    if (!devices?.items || !trapSiteData?.items) return [];

    const trapSiteMap = new Map(trapSiteData.items.map((t) => [t._id, t]));

    return devices.items.map((device) => {
      const trapSite = trapSiteMap.get(device.trap_site_id);

      return {
        key: device.uuid || "",
        deviceName: device.display_name || "N/A",
        location: trapSite?.display_name || "N/A",
        address: trapSite?.address || "N/A",
        coordinates: device?.gps
          ? `${device.gps.lat.toFixed(4)}, ${device.gps.lon.toFixed(4)}`
          : "N/A",
        wifiConnection: device.wifi || "Connect",
        status: deviceStatusTag(device),
      };
    });
  }, [devices, trapSiteData]);

  const openWifiModal = useCallback(
    (deviceUuid: string) => {
      const device = devices?.items.find((d) => d.uuid === deviceUuid);
      if (device) {
        dispatch(selectDevice(device as Device));
        dispatch(setWifiModalOpen(true));
      }
    },
    [devices, dispatch],
  );

  const sortIcon = useMemo(
    () =>
      ({ sortOrder }: { sortOrder: "ascend" | "descend" | null }) => {
        if (sortOrder === "ascend")
          return <ChevronUpIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        if (sortOrder === "descend")
          return <ChevronDownIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        return (
          <ChevronDownIcon className="w-5 h-5 opacity-0 group-hover:opacity-100 ml-1" />
        );
      },
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: "Trap ID",
        dataIndex: "deviceName",
        key: "deviceName",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          (a.deviceName || "").localeCompare(b.deviceName || ""),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.location.localeCompare(b.location),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Address",
        dataIndex: "address",
        key: "address",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.address.localeCompare(b.address),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Coordinates",
        dataIndex: "coordinates",
        key: "coordinates",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.coordinates.localeCompare(b.coordinates),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Wifi connection",
        dataIndex: "wifiConnection",
        key: "wifiConnection",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.wifiConnection.localeCompare(b.wifiConnection),
        sortIcon,
        render: (text: string, record: DeviceTableRecord) => (
          <span
            className="text-[#0EA5E9] cursor-pointer"
            onClick={() => openWifiModal(record.key)}
          >
            {text}
          </span>
        ),
      },
      {
        title: "Device",
        key: "status",
        dataIndex: "status",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          DEVICE_STATUS_LABELS[a.status].localeCompare(
            DEVICE_STATUS_LABELS[b.status],
          ),
        sortIcon,
        render: (status: DeviceStatusTag) => <StatusTag status={status} />,
      },
    ],
    [openWifiModal, sortIcon],
  );

  if (compact) {
    if (deviceData.length === 0) {
      const loading = isTrapSitesLoading || isDevicesLoading;
      return (
        <div
          role="status"
          aria-busy={loading}
          className="bg-white p-6 rounded-lg border border-[#0C4A6E] flex justify-center"
        >
          {loading ? <Spin /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      );
    }

    return (
      <ul className="trap-card-list">
        {deviceData.map((record) => (
          // Long-press raises contextmenu on Android, so Edit Trap stays
          // reachable the same way it is on a table row.
          <Dropdown
            key={record.key}
            menu={menuFor(record.key)}
            trigger={["contextMenu"]}
          >
            <li className="trap-card">
              <div className="trap-card-header">
                <div className="trap-card-name">{record.deviceName}</div>
                <div className="trap-card-aside">
                  <StatusTag status={record.status} />
                  <Dropdown menu={menuFor(record.key)} trigger={["click"]}>
                    <button
                      type="button"
                      className="trap-card-actions"
                      aria-label="Trap actions"
                    >
                      <EllipsisVerticalIcon className="w-5 h-5" />
                    </button>
                  </Dropdown>
                </div>
                <div className="trap-card-meta">
                  <div className="trap-card-location">{record.location}</div>
                  {record.address !== "N/A" && (
                    <div className="trap-card-address">{record.address}</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="trap-card-wifi"
                onClick={() => openWifiModal(record.key)}
              >
                <WifiIcon className="w-5 h-5 shrink-0" />
                <span className="truncate">{record.wifiConnection}</span>
                <ChevronRightIcon className="w-4 h-4 shrink-0 ml-auto" />
              </button>
            </li>
          </Dropdown>
        ))}
      </ul>
    );
  }

  return (
    // Until every column fits, the table scrolls inside its card rather than
    // pushing the page sideways.
    <div className="bg-white p-6 rounded-lg border border-[#0C4A6E] overflow-x-auto">
      <Table<DeviceTableRecord>
        columns={columns}
        dataSource={deviceData}
        loading={isTrapSitesLoading || isDevicesLoading}
        pagination={false}
        className="device-table traps-admin-table"
        // Right-click a trap for Edit Trap. The row key is the device uuid.
        components={{
          body: {
            row: (props: any) => wrapRow(props["data-row-key"], props),
          },
        }}
      />
    </div>
  );
};

export default TrapTable;
